/*
 * 1DS JS SDK Properties plugin, 3.1.9
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* Application.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { objDefineAccessors, getDocument, safeGetCookieMgr, getCookieValue } from "@microsoft/1ds-core-js";
export var defaultFlightIdNameSpaces = [
    "AX",
    "EX",
    "SF",
    "CS",
    "CF",
    "CT",
    "CU",
    "DC",
    "DF",
    "H5",
    "HL",
    "WS",
    "WP" // WINDOWSPHONE
];
/// <summary>Validate each flight id in appExpId against the app flight ID
/// format and create a comma seperated appExpId with valid flight ids.Ignore invalid flight IDs< /summary>
/// <param type='Object'>A list of comma seperated appExpId</param>
/// <returns type='void'>none</returns>
function _validateAppExpId(appExpIdNew, flightIdNameSpaces) {
    if (flightIdNameSpaces === void 0) { flightIdNameSpaces = defaultFlightIdNameSpaces; }
    var appExpId = null;
    if (appExpIdNew) {
        var expIdArray = appExpIdNew.split(",");
        for (var i = 0; i < expIdArray.length; i++) {
            if (_isValidAppFlightId(expIdArray[i], flightIdNameSpaces)) {
                if (!appExpId) {
                    appExpId = expIdArray[i];
                }
                else {
                    appExpId += "," + expIdArray[i];
                }
            }
            else {
                // this._traceLogger.w('Unsupported flight id format for this app expId: ' + expIdArray[i]);
            }
        }
    }
    return appExpId;
}
/// <summary>Verify an expId against the CS2.1 spec</summary>
/// <param type='Object'>expId to verify in string format</param>
/// <returns type='true'>true if expId is valid, false otherwise</returns>
function _isValidAppFlightId(appFlightId, flightIdNameSpaces) {
    if (flightIdNameSpaces === void 0) { flightIdNameSpaces = defaultFlightIdNameSpaces; }
    if (!appFlightId || appFlightId.length < 4) {
        return false;
    }
    var isValid = false;
    var MAXFLIGHTIDLENGTH = 256;
    var curNameSpace = (appFlightId.substring(0, 3)).toString().toUpperCase();
    // The prefix check must include ':', else strings starting with prefixes will slip through
    for (var i = 0; i < flightIdNameSpaces.length; i++) {
        if (flightIdNameSpaces[i] + ":" === curNameSpace && appFlightId.length <= MAXFLIGHTIDLENGTH) {
            isValid = true;
            break;
        }
    }
    return isValid;
}
var Application = /** @class */ (function () {
    function Application(propertiesConfig, core) {
        this.core = core;
        this.appExpId = null;
        // Create a copy of the defaultNamespaces
        this.flightIdNameSpaces = defaultFlightIdNameSpaces.slice(0);
        this.expIdCookieName = "Treatments";
        this._cookieMgr = safeGetCookieMgr(core);
        this._propertiesConfig = propertiesConfig;
        var doc = getDocument();
        // Add app language
        if (doc) {
            var documentElement = doc.documentElement;
            if (doc) {
                this.locale = documentElement.lang;
            }
        }
        this.env = propertiesConfig.env ? propertiesConfig.env : this._getMetaDataFromDOM("awa-")["env"];
    }
    /**
    * The application experiment Id.
    */
    Application.prototype.getExpId = function () {
        return this._propertiesConfig.expId ? this._readExpIdFromCoreData(this._propertiesConfig.expId) : this._readExpIdFromCookie();
    };
    /**
    * Retrieve a specified metadata tag value from the DOM.
    * @param prefix - Prefix to search the metatags with.
    * @returns Metadata collection/property bag
    */
    Application.prototype._getMetaDataFromDOM = function (prefix) {
        var metaElements;
        var metaData = {};
        var doc = getDocument();
        if (doc) {
            metaElements = doc && doc.querySelectorAll("meta");
            for (var i = 0; i < metaElements.length; i++) {
                var meta = metaElements[i];
                if (meta.name) {
                    var mt = meta.name.toLowerCase();
                    if (mt.indexOf(prefix) === 0) {
                        var name = meta.name.replace(prefix, "");
                        metaData[name] = meta.content;
                    }
                }
            }
        }
        return metaData;
    };
    /// <summary>Validate each flight id in appExpId against the app flight ID
    /// format and create a comma seperated appExpId with valid flight ids.Ignore invalid flight IDs< /summary>
    /// <param type='Object'>A list of comma seperated appExpId</param>
    /// <returns type='void'>none</returns>
    Application.prototype._setAppExpId = function (appExpIdNew) {
        if (appExpIdNew === this.appExpId) {
            return; // Nothing to do if seen before
        }
        // Reset to empty first. The new but invalid appExpId should not be logged and we cannot leave the old one hanging around
        // since it means old experiment id cannot represent the new experiment with an invalid appExpId.
        this.appExpId = _validateAppExpId(appExpIdNew, this.flightIdNameSpaces);
    };
    /// <summary>Get experiment ids based on flight ID string format.</summary>
    /// <returns type='true'>Comma seperated experiment ids</returns>
    Application.prototype._getAppExpId = function () {
        return this.appExpId;
    };
    Application.prototype._readExpIdFromCookie = function () {
        var cookieValue = getCookieValue(this._cookieMgr, this.expIdCookieName);
        this._setAppExpId(cookieValue);
        return this._getAppExpId();
    };
    Application.prototype._readExpIdFromCoreData = function (expId) {
        this._setAppExpId(expId);
        return this._getAppExpId();
    };
    /// <summary>Validate each flight id in appExpId against the app flight ID
    /// format and create a comma seperated appExpId with valid flight ids.Ignore invalid flight IDs< /summary>
    /// <param type='Object'>A list of comma seperated appExpId</param>
    /// <returns type='void'>none</returns>
    /**
     * Validate each flight id in appExpId against the app flight ID format and create
     * a comma seperated appExpId with valid flight ids.Ignore invalid flight IDs
     * @param appExpId - The current experiment id
     * @param appExpIdNew - The new experiment id to set (if valid)
     * @param flightIdNameSpaces - The valid set of flight id names
     * @returns The validated experiment id,
     */
    Application.validateAppExpId = _validateAppExpId;
    /**
     * Static constructor, attempt to create accessors
     */
    Application._staticInit = (function () {
        // Dynamically create get/set property accessors
        objDefineAccessors(Application.prototype, "expId", Application.prototype.getExpId);
    })();
    return Application;
}());
export { Application };
//# sourceMappingURL=Application.js.map